/**
 * TemplateSelectionModal Component
 * Simplified modal for creating projects from templates
 * Phase 7 Batch 7.3 - Proposal Frontend (Updated)
 *
 * Flow: Select template + fill org inline → Create directly → Navigate to project
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects, useCreateFromTemplate } from '@/hooks';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { OrganizationFormModal } from '@/components/forms/OrganizationFormModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, ArrowLeft, Loader2 } from 'lucide-react';

interface TemplateSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

interface SelectedTemplate {
  id: string;
  name: string;
  proposed_total?: number;
  fee_assumptions?: string;
}

export function TemplateSelectionModal({ open, onClose }: TemplateSelectionModalProps) {
  const navigate = useNavigate();

  // Template selection state
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | null>(null);

  // Form state for creating project from template
  const [organizationId, setOrganizationId] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [formError, setFormError] = useState<string>('');

  // Fetch templates
  const { data, isLoading, error } = useProjects({ is_template: true });
  const templates = data?.projects || [];

  // Create from template mutation
  const createFromTemplate = useCreateFromTemplate();

  const handleSelectTemplate = (template: SelectedTemplate) => {
    setSelectedTemplate(template);
    setProjectName(''); // Reset name to let them customize
    setOrganizationId('');
    setFormError('');
  };

  const handleBack = () => {
    setSelectedTemplate(null);
    setProjectName('');
    setOrganizationId('');
    setFormError('');
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setProjectName('');
    setOrganizationId('');
    setFormError('');
    onClose();
  };

  const handleOrganizationCreated = (org: { id: string }) => {
    setOrganizationId(org.id);
    setShowCreateOrgModal(false);
  };

  const handleCreateProject = async () => {
    if (!selectedTemplate) return;

    if (!organizationId) {
      setFormError('Please select a client');
      return;
    }

    setFormError('');

    try {
      const result = await createFromTemplate.mutateAsync({
        templateId: selectedTemplate.id,
        data: {
          organization_id: organizationId,
          name: projectName || undefined, // Use template name if not specified
        },
      });

      // Navigate to the new project
      if (result.project?.id) {
        navigate(`/projects/${result.project.id}`);
      }

      handleClose();
    } catch {
      // Error already handled by hook (toast shown)
    }
  };

  // Template selection view
  const renderTemplateGrid = () => (
    <>
      <DialogHeader>
        <DialogTitle>Select a Template</DialogTitle>
        <DialogDescription>
          Choose a template to start your new proposal with pre-configured scopes and items.
        </DialogDescription>
      </DialogHeader>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-sky-600 mx-auto mb-4" />
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 border border-destructive rounded-lg bg-destructive/10">
          <p className="text-sm text-destructive">
            Failed to load templates. Please try again later.
          </p>
        </div>
      )}

      {!isLoading && !error && templates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No Templates Available</h3>
          <p className="text-muted-foreground">
            Create templates in Settings to quickly start new proposals.
          </p>
        </div>
      )}

      {!isLoading && !error && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          {templates.map((template) => (
            <Card
              key={template.id}
              className="cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => handleSelectTemplate({
                id: template.id,
                name: template.name,
                proposed_total: template.proposed_total,
                fee_assumptions: template.fee_assumptions,
              })}
            >
              <CardHeader>
                <CardTitle className="text-base">{template.name}</CardTitle>
                <CardDescription>
                  {template.proposed_total && template.proposed_total > 0
                    ? `$${template.proposed_total.toLocaleString()} proposed`
                    : 'Template'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {template.fee_assumptions && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Fee Assumptions
                    </p>
                    <p className="text-sm line-clamp-2">{template.fee_assumptions}</p>
                  </div>
                )}

                <Button className="w-full" size="sm">
                  Select
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
      </DialogFooter>
    </>
  );

  // Create project form (when template selected)
  const renderCreateForm = () => (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <DialogTitle>Create Project from Template</DialogTitle>
            <DialogDescription>
              Creating from &ldquo;{selectedTemplate?.name}&rdquo;
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {/* Organization Selection - Required */}
        <div className="space-y-2">
          <Label htmlFor="organization" className="text-sm font-medium">
            Client <span className="text-destructive">*</span>
          </Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <SearchableOrganizationCombobox
                value={organizationId || undefined}
                onValueChange={(value) => {
                  setOrganizationId(value);
                  setFormError('');
                }}
                role="client"
                useServerSearch={true}
                placeholder="Select a client"
                className={formError ? 'border-destructive' : ''}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowCreateOrgModal(true)}
              title="Create new client"
            >
              +
            </Button>
          </div>
          {formError && (
            <p className="text-sm text-destructive">{formError}</p>
          )}
        </div>

        {/* Project Name - Optional override */}
        <div className="space-y-2">
          <Label htmlFor="projectName" className="text-sm font-medium">
            Project Name <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            id="projectName"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder={selectedTemplate?.name || 'Uses template name'}
          />
          <p className="text-xs text-muted-foreground">
            Leave blank to use the template name
          </p>
        </div>

        {/* Template Info Preview */}
        {selectedTemplate?.proposed_total && selectedTemplate.proposed_total > 0 && (
          <div className="rounded-md bg-blue-50 p-3 border border-blue-100">
            <p className="text-sm font-medium text-blue-900">Template includes:</p>
            <p className="text-sm text-sky-700">
              ${selectedTemplate.proposed_total.toLocaleString()} in proposed fees
            </p>
          </div>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button
          onClick={handleCreateProject}
          disabled={createFromTemplate.isPending}
        >
          {createFromTemplate.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Project'
          )}
        </Button>
      </DialogFooter>

      {/* Nested modal for creating new organization */}
      <OrganizationFormModal
        open={showCreateOrgModal}
        onOpenChange={setShowCreateOrgModal}
        fieldSet="minimal"
        onSave={handleOrganizationCreated}
      />
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        {selectedTemplate ? renderCreateForm() : renderTemplateGrid()}
      </DialogContent>
    </Dialog>
  );
}
